import { useNavigate, useParams } from "react-router-dom";
import MenuTop from "../../../../components/menu/top/menu-top";
import Form from "../../../../components/form/form";
import InfoComponent from "../../../../components/info/info";
import { useCallback, useEffect, useState } from "react";
import { useAlert } from "../../../../components/alert/alert";
import { SEVERITY } from "../../../../shared/interfaces/alert";
import { FormProps, FormTypes } from "../../../../shared/interfaces/form";
import Icons from "../../../../components/icons/iconts";
import {
    fetchBeneficiaryProjectService,
    updateBeneficiaryProjectService,
    fetchBeneficiariesService,
    fetchProjectsService,
    BeneficiaryProjectUpdateData,
    BeneficiaryProject
} from "../service/service";
// import "./style.css";

// Usando a interface FormSection que já existe no componente Form
interface FormSection {
  title: string;
  icon: string;
  color: string;
  fields: FormProps[];
}

export function BeneficiaryProjectEdit() {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { showAlert } = useAlert();
    const [isLoading, setIsLoading] = useState(false);
    const [beneficiaries, setBeneficiaries] = useState<any[]>([]);
    const [projects, setProjects] = useState<any[]>([]);
    const [beneficiaryProject, setBeneficiaryProject] = useState<BeneficiaryProject | null>(null);
    const [formData, setFormData] = useState<FormProps[]>([]);
    const [sections, setSections] = useState<FormSection[]>([]);

    const fetchSelectOptions = useCallback(async () => {
        try {
            const [beneficiariesData, projectsData] = await Promise.all([
                fetchBeneficiariesService(),
                fetchProjectsService()
            ]);
            
            setBeneficiaries(beneficiariesData);
            setProjects(projectsData);
            
        } catch (error: any) {
            showAlert(
                error.message || 'Erro ao carregar dados para o formulário',
                SEVERITY.ERROR
            );
        }
    }, [showAlert]);

    const fetchBeneficiaryProject = useCallback(async () => {
        if (!id) {
            showAlert('ID do vínculo não fornecido', SEVERITY.ERROR);
            navigate('/beneficiario-projeto');
            return;
        }

        try {
            setIsLoading(true);
            
            const data = await fetchBeneficiaryProjectService(id);
            
            setBeneficiaryProject(data);
            
        } catch (error: any) {
            showAlert(
                error.message || 'Erro ao carregar dados do vínculo',
                SEVERITY.ERROR
            );
            navigate('/beneficiario-projeto');
        } finally {
            setIsLoading(false);
        }
    }, [id, showAlert, navigate]);

    // Função para formatar data para exibição
    const formatDateForDisplay = (dateString: string | null | undefined): string => {
        if (!dateString) return '';
        
        try {
            const cleanDateString = dateString.split('T')[0];
            const date = new Date(cleanDateString + 'T00:00:00');
            
            if (isNaN(date.getTime())) return '';
            
            return cleanDateString;
        } catch (error) {
            console.error('Erro ao formatar data:', error);
            return '';
        }
    };

    // Função para validar e formatar datas
    const validateDateRange = (startDate: string, endDate: string): string | null => {
        if (!startDate || !endDate) return null;
        
        const start = new Date(startDate);
        const end = new Date(endDate);
        
        if (start >= end) {
            return 'Data de início deve ser anterior à data de fim';
        }
        
        const daysDifference = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
        if (daysDifference < 1) {
            return 'O período do projeto deve ter pelo menos 1 dia';
        }
        
        return null;
    };

    // Atualizar vínculo
    const handleSubmit = async (data: any) => {
        if (!id) return;

        try {
            setIsLoading(true);
            
            if (!data.project_end_date) {
                throw new Error('Data de fim é obrigatória');
            }
            
            if (!data.monthly_cost || data.monthly_cost <= 0) {
                throw new Error('Custo mensal deve ser maior que zero');
            }
            
            const updateData: BeneficiaryProjectUpdateData = {
                project_end_date: data.project_end_date,
                monthly_cost: parseFloat(data.monthly_cost.toString().replace(',', '.')),
                description: data.description || '',
                is_active: data.is_active ?? true
            };
            
            await updateBeneficiaryProjectService(id, updateData);
            
            showAlert('Vínculo beneficiário-projeto atualizado com sucesso!', SEVERITY.SUCCESS);
            navigate('/beneficiario-projeto');
            
        } catch (error: any) {
            showAlert(
                error.message || 'Erro ao atualizar vínculo beneficiário-projeto',
                SEVERITY.ERROR
            );
        } finally {
            setIsLoading(false);
        }
    };

    // Calcular duração do projeto
    const calculateProjectDuration = () => {
        if (!beneficiaryProject?.project_start_date || !beneficiaryProject?.project_end_date) {
            return null;
        }
        
        const startDate = new Date(beneficiaryProject.project_start_date);
        const endDate = new Date(beneficiaryProject.project_end_date);
        const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        const diffMonths = Math.round(diffDays / 30);
        
        return { days: diffDays, months: diffMonths };
    };

    useEffect(() => {
        if (beneficiaryProject && beneficiaries.length > 0 && projects.length > 0) {
            // Criar todos os campos primeiro para passar no data
            const allFields: FormProps[] = [
                {
                    name: 'beneficiaryId',
                    typeOfField: FormTypes.SELECT,
                    placeholder: 'Selecione o beneficiário',
                    label: 'Beneficiário *',
                    width: 50,
                    value: beneficiaryProject.beneficiaryId,
                    options: {
                        data: beneficiaries.map(beneficiary => ({
                            label: `${beneficiary.name} - ${beneficiary.type || 'Tipo não informado'}`,
                            value: beneficiary.id
                        })),
                        placeholder: 'Selecione o beneficiário'
                    },
                    rules: { required: 'Beneficiário é obrigatório' }
                },
                {
                    name: 'projectId',
                    typeOfField: FormTypes.SELECT,
                    placeholder: 'Selecione o projeto',
                    label: 'Projeto *',
                    width: 50,
                    value: beneficiaryProject.projectId,
                    options: {
                        data: projects.map(project => ({
                            label: `${project.name} - ${project.principal_investigator || 'PI não informado'}`,
                            value: project.id
                        })),
                        placeholder: 'Selecione o projeto'
                    },
                    rules: { required: 'Projeto é obrigatório' }
                },
                {
                    name: 'is_active',
                    typeOfField: FormTypes.SELECT,
                    placeholder: 'Selecione o status',
                    label: 'Status do Vínculo *',
                    width: 50,
                    value: beneficiaryProject.is_active,
                    options: {
                        data: [
                            { label: 'Ativo', value: true },
                            { label: 'Inativo', value: false }
                        ],
                        placeholder: 'Selecione o status'
                    },
                    rules: { required: 'Status é obrigatório' }
                },
                {
                    name: 'project_end_date',
                    typeOfField: FormTypes.DATE,
                    placeholder: 'DD/MM/AAAA',
                    label: 'Data de Fim do Projeto *',
                    width: 250,
                    value: formatDateForDisplay(beneficiaryProject.project_end_date),
                    rules: { required: 'Data de fim é obrigatória' }
                },
                {
                    name: 'monthly_cost',
                    typeOfField: FormTypes.CURRENCY,
                    placeholder: 'R$ 0,00',
                    label: 'Custo Mensal *',
                    width: 50,
                    value: beneficiaryProject.monthly_cost_display || (beneficiaryProject.monthly_cost / 100),
                    rules: { required: 'Custo mensal é obrigatório' }
                },
                {
                    name: 'description',
                    typeOfField: FormTypes.TEXTAREA,
                    placeholder: 'Descreva detalhes específicos sobre este vínculo, responsabilidades, observações...',
                    label: 'Descrição',
                    width: 100,
                    value: beneficiaryProject.description || '',
                    minRows: 4,
                    maxRows: 8
                }
            ];

            // Definir os campos para o array data
            setFormData(allFields);

            // Seção 1: Informações do Vínculo
            const vinculoSection: FormSection = {
                title: "INFORMAÇÕES DO VÍNCULO",
                icon: "Link",
                color: "blue",
                fields: allFields.slice(0, 3) // Primeiros 3 campos
            };

            // Seção 2: Data e Valores
            const valoresSection: FormSection = {
                title: "DATA E VALORES",
                icon: "Calendar",
                color: "green",
                fields: allFields.slice(3, 5) // Campos de data e custo
            };

            // Seção 3: Observações
            const observacoesSection: FormSection = {
                title: "OBSERVAÇÕES",
                icon: "FileText",
                color: "gray",
                fields: [allFields[5]] // Campo de descrição
            };

            setSections([vinculoSection, valoresSection, observacoesSection]);
        }
    }, [beneficiaryProject, beneficiaries, projects]);

    useEffect(() => {
        fetchSelectOptions();
        fetchBeneficiaryProject();
    }, [fetchSelectOptions, fetchBeneficiaryProject]);

    if (isLoading && !beneficiaryProject) {
        return (
            <div className="container-client beneficiary-project-edit">
                <MenuTop />
                <div className='container-client__content'>
                    <div className="beneficiary-project-edit__loading">
                        <div className="beneficiary-project-edit__loading-spinner"></div>
                        <p>Carregando dados do vínculo...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!beneficiaryProject && !isLoading) {
        return (
            <div className="container-client beneficiary-project-edit">
                <MenuTop />
                <div className='container-client__content'>
                    <div className="beneficiary-project-edit__not-found">
                        <h2 className="beneficiary-project-edit__not-found-title">
                            Vínculo não encontrado
                        </h2>
                        <p className="beneficiary-project-edit__not-found-text">
                            O vínculo solicitado não foi encontrado ou você não tem permissão para acessá-lo.
                        </p>
                        <button 
                            className="beneficiary-project-edit__back-button"
                            onClick={() => navigate('/beneficiario-projeto')}
                        >
                            <Icons iconName="ArrowBack" fontSize={16} color="white" />
                            Voltar para Lista
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-client beneficiary-project-edit">
            <MenuTop />
            
            <div className='container-client__content'>
                <div className="container-client__content__title">
                    <h1 className="container-client__content__title-text">
                        Editar Vínculo Beneficiário com o Projeto
                    </h1>
                </div>

                <div className="container-client__content__form">
                    <InfoComponent />
                    
                    {formData.length > 0 && (
                        <div className="beneficiary-project-edit__form-wrapper">
                            <Form
                                data={formData}
                                sections={sections}
                                title="Editar Vínculo Beneficiário com o Projeto"
                                onSubmitForm={handleSubmit}
                                onCancel={() => navigate('/beneficiario-projeto/listar')}
                                isLoading={isLoading}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}